import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { AppFormComponent } from '@components/forms/app-form.component';

import { defaultConfig } from 'src/app/app.config';

const { notification, pendingNotifications, sessionEstablished } = defaultConfig.appEvents;

const TARGET_FORM = 'customerNotifications';

const frm = {
  name: 'customerNotificationsBadge',
  fields: {
    unreadCount: 'unreadCount',
  },
  actions: {
    refreshCount: 'refreshCount',
  },
};

/**
 * Campana de notificaciones del encabezado de los formularios.
 *
 * Solo consulta cuántas notificaciones sin leer tiene el usuario y las presenta
 * como un badge rojo; el detalle lo atiende el formulario `customerNotifications`.
 *
 * A diferencia del resto de componentes-formulario, este vive DENTRO del
 * componente ruteado (lo hospeda `app-form-header`), lo que obliga a dos
 * desviaciones del patrón habitual:
 *
 *  1. No se suscribe a `_route.params`. El ActivatedRoute que recibe es el del
 *     formulario anfitrión, así que `processInputParams` le inyectaría el
 *     `token`, el `subject`, los `inputDataFields` y el `originToken` de ESE
 *     formulario. Se inicializa una sola vez con parámetros vacíos.
 *  2. No usa su propio `openForm`. Ese método armaría el origen del back-stack
 *     con el nombre y la ruta de la campana (que no tiene ruta), y el "Atrás"
 *     de la pantalla de notificaciones quedaría roto. La navegación se delega
 *     al formulario anfitrión, que sí sabe a dónde volver.
 */
@Component({
  standalone: true,
  selector: 'app-notifications-bell',
  // Se importa NzBadgeModule directo y NO el barrel `SHARED_MODULES`: ese barrel
  // reexporta AppFormHeaderComponent, que a su vez hospeda esta campana, y el
  // ciclo resultante deja la clase sin inicializar al evaluar los módulos.
  imports: [NzBadgeModule],
  templateUrl: './notifications-bell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./notifications-bell.component.scss'],
})
export class NotificationsBellComponent extends AppFormComponent {
  /** Formulario anfitrión; es quien abre el destino para preservar el "Atrás". */
  @Input() hostForm: any = null;

  unreadCount = 0;

  override preStart() {
    this.name = frm.name;
  }

  /**
   * Reemplaza el ngOnInit de AppBaseFormComponent para NO suscribirse a los
   * parámetros de la ruta (ver nota 1 en la doc de la clase).
   */
  override ngOnInit() {
    this.preStart();
    this.customPreProcessing();
    if (this.hasSession) {
      this.initBadge();
      return;
    }
    // El encabezado puede montarse un instante antes de que la sesión quede
    // establecida; sin sesión el gateway responde SESSION_CLOSED y el frontend
    // dispararía el modal de cierre de sesión, así que se espera.
    this.subscribeAppEvents(sessionEstablished.name, () => {
      if (this.hasSession && !this.visible) {
        this.initBadge();
      }
    });
  }

  private get hasSession(): boolean {
    return !!this._userSession?.sessionData;
  }

  private initBadge() {
    this.cleanStart();
    // `formInit` es asíncrono y la librería pone `visible = true` después de sus `await`.
    // Con OnPush y sin ngOnInit de la base, nadie marca la vista: el badge quedaría
    // invisible para siempre. Ver `marcarParaRevision` en AppBaseFormComponent.
    void Promise.resolve(this.formInit({})).then(() => this.marcarParaRevision());
  }

  override start() {
    super.start();
    this.readCount();
    this.onActionFinish(frm.actions.refreshCount, () => this.readCount());
    // Notificación entrante empujada por el servidor (SSE).
    this.subscribeAppEvents(notification.name, () => this.refresh());
    // Alguien marcó leída o eliminó una notificación en esta misma sesión.
    this.subscribeAppEvents(pendingNotifications.name, () => this.refresh());
  }

  private refresh() {
    if (this.visible) {
      this.startAction(frm.actions.refreshCount);
    }
  }

  private readCount() {
    const value = Number(this.getFieldValue(frm.fields.unreadCount));
    this.unreadCount = Number.isFinite(value) && value > 0 ? value : 0;
    // Llega desde `start()` y desde el fin de `refreshCount`; ninguna de las dos vías
    // pasa por un binding, así que hay que pedir la revisión de la vista.
    this.marcarParaRevision();
  }

  /** Solo se pinta cuando el formulario cargó bien; un error deja el header limpio. */
  get showBell(): boolean {
    return this.visible && !this.errorOccured();
  }

  openNotifications() {
    if (this.hostForm?.openForm) {
      this.hostForm.openForm(TARGET_FORM);
      return;
    }
    // Sin anfitrión no hay a dónde volver: se abre como raíz del stack.
    this._formOperation.openForm(null, { name: TARGET_FORM });
  }
}
